import { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { loginDetailsAction, loginDetailsPrompt, sendLoginDetails, type LoginDetailsSent } from "../../api/loginDetails";
import type { SubscriberMatch } from "../../api/subscriberSearch";

/**
 * Users › Customer lookup › Send login details / Reset password (owner
 * 2026-10-18/19): one confirm, then the service emails a generated password
 * to the subscriber's confirmed address. Staff never see it.
 */
export function SubscriberLoginDetailsDialog({ ns, subscriber, onClose, onSent }: { ns: string; subscriber: SubscriberMatch | null; onClose: () => void; onSent: () => void }) {
  const identity = useIdentity();
  const send = useMutation({
    mutationFn: () => sendLoginDetails(identity, ns, subscriber!.accountid),
    onSuccess: () => onSent(),
  });
  const { reset } = send;
  useEffect(() => {
    reset();
  }, [subscriber, reset]);
  if (!subscriber) return null;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {loginDetailsAction(subscriber).label} · {subscriber.name || subscriber.mobile}
      </DialogTitle>
      <DialogContent>
        <LoginDetailsView
          m={subscriber}
          sent={send.data ?? null}
          error={send.error ? (send.error instanceof Error ? send.error.message : String(send.error)) : null}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{send.data ? "Close" : "Cancel"}</Button>
        {!send.data && (
          <Button variant="contained" disabled={send.isPending} onClick={() => send.mutate()}>
            {send.isPending ? "Sending…" : loginDetailsAction(subscriber).label}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** The dialog's body without its hooks, so a test can render it. */
export function LoginDetailsView({ m, sent, error }: { m: SubscriberMatch; sent: LoginDetailsSent | null; error: string | null }) {
  if (sent) {
    return (
      <Alert severity="success">
        {sent.login_details === "confirm_sent"
          ? `Confirm link sent to ${sent.to}. The login details follow once the subscriber opens it.`
          : `Login details emailed to ${sent.to}.`}
      </Alert>
    );
  }
  return (
    <>
      <Typography variant="body2">{loginDetailsPrompt(m)}</Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </>
  );
}
